import React from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {Shield, Lock, Eye, Database, Mail, UserCheck} from 'lucide-react';

const PrivacyPolicyPage: React.FC = () => {
  const location = useLocation();
  const currentLang = location.pathname.startsWith('/en') ? 'en' : 'zh';

  const content = {
    zh: {
      title: '隱私政策 | ONGU Studio',
      description: 'ONGU Studio 致力於保護您的個人隱私，了解我們如何收集、使用與保護您的資料',
      heading: '隱私政策',
      subtitle: 'Privacy Policy',
      lastUpdated: '最後更新：2024 年 1 月',
      sections: [
        {
          icon: Shield,
          title: '資料收集',
          content: [
            '我們收集的個人資料可能包括：姓名、電子郵件地址、電話號碼、配送地址、付款資訊。',
            '當您瀏覽我們的網站時，我們可能會自動收集技術資訊，如 IP 地址、瀏覽器類型、造訪時間等。',
            '透過 Cookie 和類似技術，我們收集有關您使用我們服務的資訊，以改善用戶體驗。'
          ]
        },
        {
          icon: Lock,
          title: '資料使用',
          content: [
            '處理您的訂單並提供您購買的產品或服務。',
            '改善我們的網站、產品和服務。',
            '向您發送有關訂單、產品更新或促銷活動的通知（您可以隨時取消訂閱）。',
            '防止欺詐和確保網站安全。'
          ]
        },
        {
          icon: Database,
          title: '資料保護',
          content: [
            '我們採用業界標準的安全措施來保護您的個人資料，包括加密技術和安全伺服器。',
            '僅授權人員可訪問您的個人資料，並且這些人員需遵守保密義務。',
            '我們定期審查資料保護措施，以確保資料安全。'
          ]
        },
        {
          icon: Eye,
          title: '資料分享',
          content: [
            '我們不會出售、交易或出租您的個人資料給第三方。',
            '我們可能與信任的合作夥伴分享資料，以完成訂單處理、物流配送或付款處理（例如物流公司、金流服務商）。',
            '在法律要求或保護我們權利時，我們可能會披露您的資訊。'
          ]
        },
        {
          icon: UserCheck,
          title: '您的權利',
          content: [
            '訪問權：您有權要求查看我們持有的關於您的個人資料。',
            '更正權：您可以要求更正不準確或不完整的個人資料。',
            '刪除權：在某些情況下，您可以要求刪除您的個人資料。',
            '反對權：您可以反對我們處理您的個人資料用於特定目的（如行銷）。'
          ]
        },
        {
          icon: Mail,
          title: '聯絡我們',
          content: [
            '如果您對本隱私政策有任何疑問或需要行使您的權利，請透過以下方式聯繫我們：',
            '電子郵件：privacy@ongucoffee.com',
            '電話：+886-2-2345-6789',
            '地址：台北市信義區某某路 100 號'
          ]
        }
      ]
    },
    en: {
      title: 'Privacy Policy | ONGU Studio',
      description: 'ONGU Studio is committed to protecting your personal privacy. Learn how we collect, use, and protect your data',
      heading: 'Privacy Policy',
      subtitle: '隱私政策',
      lastUpdated: 'Last Updated: January 2024',
      sections: [
        {
          icon: Shield,
          title: 'Data Collection',
          content: [
            'The personal data we collect may include: name, email address, phone number, shipping address, payment information.',
            'When you browse our website, we may automatically collect technical information such as IP address, browser type, visit time, etc.',
            'Through cookies and similar technologies, we collect information about your use of our services to improve user experience.'
          ]
        },
        {
          icon: Lock,
          title: 'Data Use',
          content: [
            'Process your orders and provide the products or services you purchase.',
            'Improve our website, products, and services.',
            'Send you notifications about orders, product updates, or promotional activities (you can unsubscribe at any time).',
            'Prevent fraud and ensure website security.'
          ]
        },
        {
          icon: Database,
          title: 'Data Protection',
          content: [
            'We employ industry-standard security measures to protect your personal data, including encryption technology and secure servers.',
            'Only authorized personnel can access your personal data, and these personnel must comply with confidentiality obligations.',
            'We regularly review data protection measures to ensure data security.'
          ]
        },
        {
          icon: Eye,
          title: 'Data Sharing',
          content: [
            'We do not sell, trade, or rent your personal data to third parties.',
            'We may share data with trusted partners to complete order processing, logistics delivery, or payment processing (e.g., logistics companies, payment service providers).',
            'We may disclose your information when required by law or to protect our rights.'
          ]
        },
        {
          icon: UserCheck,
          title: 'Your Rights',
          content: [
            'Right of Access: You have the right to request to view the personal data we hold about you.',
            'Right to Rectification: You can request correction of inaccurate or incomplete personal data.',
            'Right to Erasure: In certain circumstances, you can request deletion of your personal data.',
            'Right to Object: You can object to our processing of your personal data for specific purposes (such as marketing).'
          ]
        },
        {
          icon: Mail,
          title: 'Contact Us',
          content: [
            'If you have any questions about this privacy policy or need to exercise your rights, please contact us:',
            'Email: privacy@ongucoffee.com',
            'Phone: +886-2-2345-6789',
            'Address: No. 100, XX Road, Xinyi District, Taipei City'
          ]
        }
      ]
    }
  };

  const t = content[currentLang];

  return (
    <>
      <Helmet>
        <title>{t.title}</title>
        <meta name="description" content={t.description} />
        <link rel="canonical" href={`https://www.ongucoffee.com/${currentLang}/privacy`} />
        <link rel="alternate" hreflang="zh-Hant" href="https://www.ongucoffee.com/zh/privacy" />
        <link rel="alternate" hreflang="en" href="https://www.ongucoffee.com/en/privacy" />
        <link rel="alternate" hreflang="x-default" href="https://www.ongucoffee.com/zh/privacy" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center mb-6">
              <Shield size={40} className="text-gray-400 dark:text-gray-500" />
            </div>
            <h1 className="text-4xl md:text-5xl font-extralight tracking-wider text-gray-800 dark:text-gray-200 mb-2">
              {t.heading}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 font-light uppercase tracking-widest mb-6">
              {t.subtitle}
            </p>
            <div className="w-16 h-px bg-gray-400 dark:bg-gray-500 mx-auto mb-6"></div>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              {t.lastUpdated}
            </p>
          </div>

          {/* Content Sections */}
          <div className="space-y-12">
            {t.sections.map((section, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-8 transition-all duration-300 hover:shadow-lg"
              >
                {/* Section Header */}
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-10 h-10 bg-gray-100 dark:bg-gray-700 mr-4">
                    <section.icon size={20} className="text-gray-600 dark:text-gray-400" />
                  </div>
                  <h2 className="text-xl font-light tracking-wide text-gray-800 dark:text-gray-200 uppercase">
                    {section.title}
                  </h2>
                </div>

                {/* Section Content */}
                <div className="space-y-4 pl-14">
                  {section.content.map((paragraph, pIndex) => (
                    <p
                      key={pIndex}
                      className="text-gray-600 dark:text-gray-400 leading-relaxed font-light"
                    >
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Footer Note */}
          <div className="mt-16 p-6 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600">
            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed font-light text-center">
              {currentLang === 'zh'
                ? '本隱私政策可能會不定期更新。我們建議您定期查看本頁面以了解任何變更。繼續使用我們的服務即表示您接受本隱私政策。'
                : 'This privacy policy may be updated from time to time. We recommend that you regularly review this page to stay informed of any changes. Continued use of our services indicates your acceptance of this privacy policy.'}
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicyPage;